import { Component, OnDestroy, ViewChild } from '@angular/core';
import { Events, NavController, NavParams, Platform } from 'ionic-angular';
import { Subject } from 'rxjs/Subject';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { PageValidationType, ValidationResult } from '../../../models/projects/validation.models';
import { MorningMinutesEditor } from '../../../models/morning-minutes/morning-minutes.models';
import { LockedDayPermissions, MorningMinutesPermissions } from '../../../models/permissions/permission.models';
import { catchError, debounce, } from 'rxjs/operators';
import { timer } from 'rxjs/observable/timer';
import { PermissionsService } from '../../../services/permissions/permissions.service';
import { DateService } from '../../../services/date/date.service';
import { DailyLogService } from '../../../services/daily-log/daily-log.service';
import { LockedDayService } from '../../../services/locked-day/locked-day.service';
import { ValidationService } from '../../../services/validation/validation.service';
import { MorningMinutesService } from '../../../services/morning-minutes/morning-minutes.service';
import { LockDayFilters } from '../../../models/lock-day.models';
import { DateParserService } from '../../../services/daily-log/daily-log-time/date-parser-service';
import { UpdatedSetupSummary } from '../../../models/setup/setup.models';
import { CompleteDayService } from '../../../services/complete-day/complete-day.service';
import { TimeZoneService } from '../../../services/time-zone/time-zone.service';
import { NavHelperService } from '../../../services/nav/nav-helper.service';
import { MorningMinutesReportPreviewPage } from '../morning-minutes-report-preview/morning-minutes-report-preview';
import { cloneDeep } from 'lodash';
import { DailyLogDatePickerComponent } from '../../shared/components/daily-log-date-picker/daily-log-date-picker';

@Component({
  selector: 'app-morning-minutes-component',
  templateUrl: 'morning-minutes-component.html',
})
export class MorningMinutesComponent implements OnDestroy {
  @ViewChild('dailyLogDatePickerComponent') dailyLogDatePickerComponent: DailyLogDatePickerComponent;

  constructor(public navCtrl: NavController,
              public navParams: NavParams,
              public platform: Platform,
              public events: Events,
              private permissionsService: PermissionsService,
              private dateService: DateService,
              private dailyLogService: DailyLogService,
              private lockedDayService: LockedDayService,
              private validationService: ValidationService,
              private morningMinutesService: MorningMinutesService,
              private dateParserService: DateParserService,
              private completeDayService: CompleteDayService,
              private timeZoneService: TimeZoneService,
              public navHelperService: NavHelperService
  ) {
    this.navHelperService.currentSetupConfigured.subscribe(currentSetupConfigured => {
      this.currentSetupConfigured = currentSetupConfigured;
    });

    this.events.subscribe('report generated', async () => {
      this.amendingMorningMinutes = false;
      await this.loadData(true);
    });
  }

  public mask: Array<string | RegExp> = [/[0-2]/, /\d/, ':', /[0-5]/, /\d/];
  morningMinutesEditor: MorningMinutesEditor = new MorningMinutesEditor();
  morningMinutesEditorCopy: MorningMinutesEditor = new MorningMinutesEditor();
  morningMinutesEditorForSave: MorningMinutesEditor = new MorningMinutesEditor(); // we mutate dates before sending to api, so we need separate model for this
  meetingTimePtDate: PtLuxonDate;
  dailyLogDate: PtLuxonDate = new PtLuxonDate();
  validationResult: ValidationResult = new ValidationResult();
  $morningMinutesChange: Subject<MorningMinutesEditor> = new Subject<MorningMinutesEditor>();
  debounceActive = false;
  leavingPage: boolean;
  latestTransactionId: number;
  popFn = null;
  generatingReport: boolean;
  isWeb: boolean;
  setupSummary: UpdatedSetupSummary;
  currentDate: PtLuxonDate = new PtLuxonDate();
  dayLockedByDate: boolean = false;
  lockable: boolean = false;
  dayCompleted: boolean;
  currentSetupConfigured: boolean;

  firstLoadComplete: boolean = false;
  dateBeforeInitialSetup: boolean;
  loading: boolean;
  validationLoading: boolean;
  permissions: MorningMinutesPermissions = new MorningMinutesPermissions();
  lockedDayPermissions: LockedDayPermissions = new LockedDayPermissions();
  amendingMorningMinutes: boolean;

  ngOnInit() {
    this.loading = true;
    this.isWeb = this.platform.is('core');
    this.ionViewEnter();
  }

  ngOnDestroy(): void {
    this.events.unsubscribe('report generated');
  }

  async ionViewEnter() {
    this.validationLoading = true;
    await this.loadData(true);
    this.setupSaveDebounce();

    this.permissions = await this.permissionsService.morningMinutesPermissions();
    this.lockedDayPermissions = await this.permissionsService.lockedDayPermissions(this.dailyLogDate);

    this.navHelperService.warned = false;
    this.navHelperService.showWarning = false;

    this.navHelperService.triedToSave = false;
    this.popFn = this.navCtrl.pop;

    this.navCtrl.pop = (): Promise<any> => {
      if (!this.generatingReport) {
        this.navHelperService.triedToSave = true;

        if (this.navHelperService.warned || this.navHelperService.unsavedChanges === false) {
          this.navHelperService.unsavedChanges = false;
          this.navCtrl.pop = this.popFn;
          this.navCtrl.pop();
          return;
        }

        this.navHelperService.showWarning = true;
        this.navHelperService.warned = true;
        this.navCtrl.pop = this.popFn;
        return;
      } else {
        if (this.navHelperService.changesSaved) {
          this.amendingMorningMinutes = false;
          this.navHelperService.warned = true;
        }

        this.navCtrl.pop = this.popFn;
        this.navCtrl.pop();
      }
    };
  }

  // this gets called via the ionViewWillLeave in the parent weather-log.ts
  // ensures that data gets saved correctly before the user leaves without having to wait for the debounce
  saveBeforeLeavingComponent() {
    this.leavingPage = true; // stop debounce from running after this save

    if (this.debounceActive) {
      this.morningMinutesService.saveEditor(cloneDeep(this.morningMinutesEditorForSave));
    }
  }

  // used for when the user views the validation and then returns to the weather log - because leavingPage will be true if they view validation
  reEnterComponent() {
    this.leavingPage = false;
  }

  ionViewDidLeave() {
    this.navHelperService.warned = true;
    this.navHelperService.showWarning = false;
  }

  setupSaveDebounce() {
    this.$morningMinutesChange
      .pipe(
        catchError(err => {
          this.debounceActive = false;
          throw (err.error);
        }),
        debounce(() => timer(2000)))
      .subscribe(async val => {
        if (!this.leavingPage) {
          await this.morningMinutesService.saveEditor(cloneDeep(val));
          this.morningMinutesEditorForSave = null;
          this.debounceActive = false;
        }
      });
  }

  async dayLockedForEditing() {
    this.checkMorningMinutesLocked();
  }

  async changesCancelled() {
    this.firstLoadComplete = false;
    this.loading = true;
    await this.loadData(true);
  }

  async changesSaved() {
    await this.loadData(false);
  }

  async sendToDebounce(editor: MorningMinutesEditor) {
    this.debounceActive = true;
    let editorClone = cloneDeep(editor);

    this.morningMinutesEditorForSave = editorClone;
    this.$morningMinutesChange.next(editorClone);
  }

  async editorChanged() {
    if (this.amendingMorningMinutes) {
      await this.morningMinutesService.saveMorningMinutesEditorLocally(this.morningMinutesEditor);
    } else {
      await this.sendToDebounce(this.morningMinutesEditor);
    }
  }

  async loadData(refresh: boolean) {
    if (refresh) {
      this.loading = true;
    }

    this.firstLoadComplete = false;
    this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();

    await this.checkMorningMinutesLocked();

    if (refresh) {
      // get data from api
      await this.morningMinutesService.getAndUpdateMorningMinutesData(this.dailyLogDate);
    }

    this.morningMinutesEditor = await this.morningMinutesService.getMorningMinutesDataForDay(this.dailyLogDate);

    if (this.morningMinutesEditor.meetingTime) {
      this.meetingTimePtDate = new PtLuxonDate(this.morningMinutesEditor.meetingDate);
    }

    if (this.dateBeforeInitialSetup === false && (!this.morningMinutesEditor || !this.morningMinutesEditor.id)) {
      this.morningMinutesEditorForSave = await this.initialiseMorningMinutesEditor();
      this.morningMinutesEditor = cloneDeep(this.morningMinutesEditorForSave);
    }

    if (refresh) {
      this.validationResult = new ValidationResult();
      this.validationLoading = true;
      this.loading = true;

      await this.permissionsService.updateLockedDayFunctionalPermissions(this.lockedDayPermissions, this.dailyLogDate);

      this.validationService.validateMorningMinutes(this.dailyLogDate, this.validationResult)
        .then(appValidation => {
          this.validationResult = appValidation;
          this.validationLoading = false;
        });

      this.firstLoadComplete = true;
    }

    this.loading = false;
  }

  async checkMorningMinutesLocked() {
    const filters = new LockDayFilters();
    filters.date = this.dailyLogDate.dateTime;

    let lockedSummary = await this.lockedDayService.checkDay(filters);

    this.dayLockedByDate = lockedSummary.lockedByDate;
    this.lockable = lockedSummary.lockable;

    this.completeDayService.checkDay(filters).then(result => {
      this.dayCompleted = result;
    });

    this.currentDate = new PtLuxonDate(lockedSummary.latestDate);
  }

  private async initialiseMorningMinutesEditor(): Promise<MorningMinutesEditor> {
    const existingEditor = await this.morningMinutesService.getUpdatedMorningMinutesData(this.dailyLogDate);
    const editor = existingEditor ? existingEditor : new MorningMinutesEditor();
    editor.commsPhone = true;
    editor.commsInternet = true;
    editor.commsEmail = true;
    editor.commsNetwork = true;

    editor.date = this.dailyLogDate.dateTime;

    return editor;
  }

  otherPagesNotComplete(): boolean {
    if (this.validationResult.errors.some(e => e.pageValidationType != null
      && e.pageValidationType !== PageValidationType.DailyObservations
      && e.pageValidationType !== PageValidationType.FieldPositioning
      && e.pageValidationType !== PageValidationType.VoyageLog)) {
      return true;
    }

    return false;
  }

  openReportPreview() {
    this.navHelperService.warned = true;
    this.generatingReport = true;
    this.navCtrl.push(MorningMinutesReportPreviewPage, {
      date: this.dailyLogDate,
      editor: this.morningMinutesEditor,
      amending: this.amendingMorningMinutes
    });
  }

  amendReport() {
    this.amendingMorningMinutes = true;
    this.morningMinutesEditorCopy = cloneDeep(this.morningMinutesEditor);
    this.navHelperService.unsavedChanges = true;
  }

  cancelAmendments() {
    this.amendingMorningMinutes = false;
    this.morningMinutesEditor = this.morningMinutesEditorCopy;
    this.morningMinutesService.saveMorningMinutesEditorLocally(this.morningMinutesEditor);
    this.navHelperService.unsavedChanges = false;
    this.navHelperService.warned = false;
    this.navHelperService.showWarning = false;
  }

  regexChange(inputTime: string) {
    if (inputTime.charAt(0) === '2') {
      this.mask[1] = new RegExp('[0-3]');
    } else {
      this.mask[1] = new RegExp('\\d');
    }
  }

  async timeInputChange() {
    const allowed = await this.dateService.timeAllowed(this.morningMinutesEditor.meetingTime, this.dailyLogDate.dateTime);

    if (!allowed) {
      return;
    }

    if (!this.dateService.timeIsValid(this.morningMinutesEditor.meetingTime, true)) {
      this.resetRegex();
      this.morningMinutesEditor.meetingTime = null;
      this.morningMinutesEditor.meetingDate = null;
      this.editorChanged();
      return;
    }

    this.meetingTimePtDate = await this.dateService.setPtLuxonDateFromUserEnteredString(this.morningMinutesEditor.meetingTime, this.dailyLogDate.dateTime);
    this.morningMinutesEditor.meetingDate = this.meetingTimePtDate.dateTime;
    this.morningMinutesEditor.meetingTimeOffset = this.meetingTimePtDate.dateTime.offset / 60;

    if (this.meetingTimePtDate.offsetNotNull()) {
      this.editorChanged();
    }
  }

  resetRegex() {
    this.mask = [/[0-2]/, /\d/, ':', /[0-5]/, /\d/];
  }

  async offsetSelected(ptLuxonDate: PtLuxonDate) {
    this.morningMinutesEditor.meetingTimeOffset = this.meetingTimePtDate.dateTime.offset / 60;
    this.morningMinutesEditor.meetingDate = this.meetingTimePtDate.dateTime;

    this.editorChanged();
  }

  dateBeforeInitialSetupChanged($event: boolean) {
    this.dateBeforeInitialSetup = $event;
  }

  goToActiveLogDay() {
    this.dailyLogDatePickerComponent.goToActiveLogDay();
  }

  dateLoadingStarted() {
    this.firstLoadComplete = false;
  }

  dateLoadingFinished() {
    this.firstLoadComplete = true;
  }
}



// WEBPACK FOOTER //
// ./src/pages/morning-minutes/morning-minutes-component/morning-minutes-component.ts